import { useQueryClient } from "@tanstack/react-query";
import { App as AntdApp } from "antd";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { IDLE_POLL_MS, isIdleExpired, markRequest } from "../api/activity";
import { ApiError, apiFetch, apiFetchWithResponse, onSessionEvent } from "../api/client";
import { isTokenExpired, tokenStore } from "../api/token";
import type { AuthenticatedUser, LoginResponse } from "../api/types";

const USER_STORAGE_KEY = "myspeechplan.user";

/**
 * "Go to the main page" on the pending-invitations screen only dismisses it for
 * the current login — the invitations stay open until they are answered. The
 * old application kept that in its server session; this API has none, so it
 * lives in sessionStorage: gone with the tab, and gone at the next login.
 */
const INVITATIONS_DISMISSED_KEY = "myspeechplan.invitationsDismissed";

function readInvitationsDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(INVITATIONS_DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
}

function storeInvitationsDismissed(dismissed: boolean): void {
  try {
    if (dismissed) {
      window.sessionStorage.setItem(INVITATIONS_DISMISSED_KEY, "true");
    } else {
      window.sessionStorage.removeItem(INVITATIONS_DISMISSED_KEY);
    }
  } catch {
    /* ignore */
  }
}

export type AuthStatus = "anonymous" | "authenticated";

interface AuthContextValue {
  status: AuthStatus;
  user: AuthenticatedUser | null;
  /** Set when the API answered 403 with policy_confirmation_needed. */
  privacyPolicyRequired: boolean;
  /** True once the user has sent the pending-invitations screen away. */
  pendingInvitationsDismissed: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Drops the local session without calling /logout. For the third way a
   * session can end: a role change the server already acted on, which answers
   * 200 with `login_required: true` and a revoked token. Calling logout() there
   * would fire a request with a token that is gone.
   */
  endSession: (notice?: string) => void;
  clearPrivacyPolicyRequirement: () => void;
  dismissPendingInvitations: () => void;
  /**
   * The account's interface language, after main#change_language has stored it.
   * The cached user is what the client reads its language from on the next
   * load, so it has to follow — otherwise a reload would put the old language
   * back and undo the change.
   */
  setUserLanguage: (languageId: number) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
  return value;
}

function readStoredUser(): AuthenticatedUser | null {
  try {
    const raw = window.localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthenticatedUser) : null;
  } catch {
    return null;
  }
}

function storeUser(user: AuthenticatedUser | null): void {
  try {
    if (user) {
      window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(USER_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * The stored token, unless it has already expired. Coming up "authenticated"
 * with a dead token only produces a UI that is logged out again at the first
 * click; dropping it here starts at the login screen instead.
 */
function liveToken(): string | null {
  const token = tokenStore.get();
  if (!token) return null;

  if (isTokenExpired(token)) {
    tokenStore.clear();
    return null;
  }

  return token;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { message } = AntdApp.useApp();
  const queryClient = useQueryClient();

  // The token decides whether we are logged in; the stored user is only the
  // cached display data that goes with it.
  const [user, setUser] = useState<AuthenticatedUser | null>(() =>
    liveToken() ? readStoredUser() : null,
  );
  const [status, setStatus] = useState<AuthStatus>(() =>
    liveToken() ? "authenticated" : "anonymous",
  );
  const [privacyPolicyRequired, setPrivacyPolicyRequired] = useState(false);
  const [pendingInvitationsDismissed, setPendingInvitationsDismissed] = useState(
    readInvitationsDismissed,
  );

  // Avoids a second "session expired" toast when several requests fail at once.
  const endingRef = useRef(false);

  const endSession = useCallback(
    (notice?: string) => {
      if (endingRef.current) return;
      endingRef.current = true;

      tokenStore.clear();
      storeUser(null);
      storeInvitationsDismissed(false);
      setUser(null);
      setStatus("anonymous");
      setPrivacyPolicyRequired(false);
      setPendingInvitationsDismissed(false);

      if (notice) message.warning(notice);

      // Release the guard once the current batch of failures has drained.
      window.setTimeout(() => {
        endingRef.current = false;
      }, 0);
    },
    [message],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const { data, response } = await apiFetchWithResponse<LoginResponse>("/login", {
        method: "POST",
        anonymous: true,
        body: { user: { email, password } },
      });

      // devise-jwt returns the token in the Authorization response header. The
      // browser only lets us read it because rack-cors lists it under `expose`
      // (config/initializers/cors.rb) — if this is ever null, check that first.
      const header = response.headers.get("Authorization");
      if (!header) {
        throw new Error(
          "Login succeeded but no Authorization header was readable. " +
            "Check that rack-cors exposes it.",
        );
      }

      // A session starts on an empty cache. Every list this application shows
      // is answered per account and per role — the navigation included — and
      // with a staleTime of 30 seconds the previous session's answers would be
      // handed out as fresh ones to the next. That is what made a support login
      // ("log in as") come up with the previous user's sidebar and data until
      // the browser window was reloaded by hand.
      queryClient.clear();

      tokenStore.set(header);
      storeUser(data.status.user);
      storeInvitationsDismissed(false);
      setUser(data.status.user);
      setStatus("authenticated");
      setPrivacyPolicyRequired(false);
      setPendingInvitationsDismissed(false);
      endingRef.current = false;
      markRequest();
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch("/logout", { method: "DELETE" });
    } catch (error) {
      // A 401 here just means the session was already gone — the local state
      // still has to be cleared, so this is not worth surfacing.
      if (!(error instanceof ApiError)) throw error;
    } finally {
      endSession();
    }
  }, [endSession]);

  // Global reaction to what the API reports, so no screen has to handle it.
  useEffect(
    () =>
      onSessionEvent((event, error) => {
        if (event === "unauthorized") {
          endSession(error.message);
        } else if (event === "privacy-policy-required") {
          setPrivacyPolicyRequired(true);
        }
      }),
    [endSession],
  );

  // Close the session locally once the server's idle window has passed, instead
  // of leaving a UI up that only looks logged in.
  useEffect(() => {
    if (status !== "authenticated") return;

    const timer = window.setInterval(() => {
      if (isIdleExpired()) endSession();
    }, IDLE_POLL_MS);

    return () => window.clearInterval(timer);
  }, [status, endSession]);

  const dismissPendingInvitations = useCallback(() => {
    storeInvitationsDismissed(true);
    setPendingInvitationsDismissed(true);
  }, []);

  const setUserLanguage = useCallback((languageId: number) => {
    setUser((current) => {
      if (!current || current.language_id === languageId) return current;

      const updated = { ...current, language_id: languageId };
      storeUser(updated);
      return updated;
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      privacyPolicyRequired,
      pendingInvitationsDismissed,
      login,
      logout,
      endSession,
      clearPrivacyPolicyRequirement: () => setPrivacyPolicyRequired(false),
      dismissPendingInvitations,
      setUserLanguage,
    }),
    [
      status,
      user,
      privacyPolicyRequired,
      pendingInvitationsDismissed,
      login,
      logout,
      endSession,
      dismissPendingInvitations,
      setUserLanguage,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
